import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { ArrowRight, ArrowLeft, ExternalLink, Sparkles, RotateCcw } from "lucide-react";

/**
 * "Puis-je passer ce challenge ?" — 5-question quiz that recommends
 * the top prop firms for the user's profile, with affiliate links.
 */

// Duplicated minimal firm data (kept in sync with PropFirmCompare.jsx).
const FIRMS = [
  { id: "ftmo",         name: "FTMO",         flag: "🇨🇿", maxDD: 10, dailyDD: 5, price: 540, split: 90, payoutDays: 14, minDays: 4,  best: ["swing","day","prop"] },
  { id: "fundednext",   name: "FundedNext",   flag: "🌐", maxDD: 10, dailyDD: 5, price: 549, split: 95, payoutDays: 7,  minDays: 5,  best: ["day","scalp","prop"] },
  { id: "the5ers",      name: "The 5%ers",    flag: "5️⃣", maxDD: 4,  dailyDD: 4, price: 260, split: 100, payoutDays: 30, minDays: 10, best: ["swing","long_term"] },
  { id: "myforexfunds", name: "MyForexFunds", flag: "💱", maxDD: 8,  dailyDD: 5, price: 599, split: 90, payoutDays: 14, minDays: 0,  best: ["scalp","day"] },
  { id: "topstep",      name: "TopStep",      flag: "🎯", maxDD: 3,  dailyDD: 2, price: 165, split: 100, payoutDays: 8, minDays: 0,  best: ["day","futures"] },
  { id: "e8",           name: "E8 Funding",   flag: "8️⃣", maxDD: 8,  dailyDD: 5, price: 588, split: 80, payoutDays: 14, minDays: 5,  best: ["day","swing"] },
  { id: "ftuk",         name: "FTUK",         flag: "🇬🇧", maxDD: 10, dailyDD: 5, price: 599, split: 80, payoutDays: 30, minDays: 0,  best: ["swing","prop"] },
  { id: "fundingpips",  name: "FundingPips",  flag: "💧", maxDD: 10, dailyDD: 5, price: 449, split: 90, payoutDays: 7,  minDays: 3,  best: ["day","scalp"] },
];

const QUESTIONS = [
  { id: "style", label: "Quel est votre style de trading ?",
    options: [
      { value: "scalp",     label: "Scalping (trades < 15 min)" },
      { value: "day",       label: "Day trading (intraday)" },
      { value: "swing",     label: "Swing (positions plusieurs jours)" },
      { value: "long_term", label: "Long terme (semaines à mois)" },
    ]},
  { id: "time",  label: "Combien de temps pouvez-vous trader par semaine ?",
    options: [
      { value: "full",     label: "Temps plein (30h+)" },
      { value: "part",     label: "Mi-temps (10-20h)" },
      { value: "light",    label: "Léger (< 5h, week-end)" },
    ]},
  { id: "risk",  label: "Quelle est votre tolérance au drawdown ?",
    options: [
      { value: "strict",   label: "Très strict — je préfère petites DD" },
      { value: "normal",   label: "Normale — 5-8% acceptable" },
      { value: "large",    label: "Large — je supporte 10%+" },
    ]},
  { id: "budget", label: "Combien pouvez-vous investir dans le challenge (100k) ?",
    options: [
      { value: "low",      label: "< 300 $" },
      { value: "medium",   label: "300-550 $" },
      { value: "high",     label: "550+ $ (peu importe)" },
    ]},
  { id: "priority", label: "Qu'est-ce qui compte le plus pour vous ?",
    options: [
      { value: "payout",   label: "Payouts rapides" },
      { value: "split",    label: "Le meilleur split de profits" },
      { value: "safe",     label: "Réputation & sécurité" },
      { value: "instant",  label: "Pas de challenge, funding direct" },
    ]},
];

function scoreFirms(answers) {
  return FIRMS.map(f => {
    let score = 0;
    // Style match
    if (answers.style && f.best.includes(answers.style)) score += 30;
    // Time
    if (answers.time === "light" && f.minDays <= 3) score += 15;
    if (answers.time === "full") score += 10;
    // Risk
    if (answers.risk === "strict" && f.maxDD <= 5) score += 20;
    if (answers.risk === "normal" && f.maxDD >= 5 && f.maxDD <= 8) score += 20;
    if (answers.risk === "large" && f.maxDD >= 8) score += 20;
    // Budget
    if (answers.budget === "low"    && f.price < 300)  score += 25;
    if (answers.budget === "medium" && f.price >= 300 && f.price <= 550) score += 25;
    if (answers.budget === "high") score += 10;
    // Priority
    if (answers.priority === "payout" && f.payoutDays <= 10) score += 20;
    if (answers.priority === "split"  && f.split >= 90)  score += 20;
    if (answers.priority === "safe"   && ["ftmo","topstep","the5ers"].includes(f.id)) score += 20;
    if (answers.priority === "instant" && f.id === "ftuk") score += 30;
    return { ...f, score };
  }).sort((a, b) => b.score - a.score);
}

export default function PropFirmQuiz() {
  usePageMeta({
    title: "Quiz — Quelle prop firm est faite pour vous ? · ANATRADEX",
    description: "5 questions pour découvrir la prop firm idéale selon votre style, votre budget et votre profil de risque.",
  });

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [aff, setAff] = useState({});

  useEffect(() => {
    fetch(`${window.location.origin}/api/public/affiliates`)
      .then(r => r.json())
      .then(d => setAff(d.ids || {}))
      .catch(() => {});
  }, []);

  const isDone = step >= QUESTIONS.length;
  const q = QUESTIONS[step];

  const choose = (val) => {
    setAnswers({ ...answers, [q.id]: val });
    setStep(step + 1);
  };
  const back = () => step > 0 && setStep(step - 1);
  const reset = () => { setAnswers({}); setStep(0); };

  const buildLink = (f) => {
    const ref = aff[f.id] || "ANATRADEX";
    const base = { ftmo:"ftmo.com", fundednext:"fundednext.com", the5ers:"the5ers.com",
      myforexfunds:"myforexfunds.com", topstep:"topstep.com", e8:"e8markets.com",
      ftuk:"ftuk.com", fundingpips:"fundingpips.com" }[f.id];
    return `https://${base}/?ref=${ref}`;
  };

  const top3 = isDone ? scoreFirms(answers).slice(0, 3) : [];

  return (
    <div className="min-h-screen bg-bg text-white/85" data-testid="quiz-page">
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-accent-blue to-blue-600 flex items-center justify-center font-bold text-lg">P</div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">Prop Firm Quiz</div>
            </div>
          </Link>
          <Link to="/prop-firms" className="text-sm text-white/60 hover:text-white">← Voir le comparateur</Link>
        </div>
      </nav>

      <section className="max-w-3xl mx-auto px-6 py-16">
        {!isDone ? (
          <>
            <div className="text-xs uppercase tracking-[0.2em] text-accent-blue/80 mb-3">
              Question {step + 1} / {QUESTIONS.length}
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-8" data-testid="quiz-question">{q.label}</h1>

            {/* Progress */}
            <div className="h-1 bg-white/5 rounded-full mb-8 overflow-hidden">
              <div className="h-full bg-accent-blue transition-all"
                   style={{ width: `${((step) / QUESTIONS.length) * 100}%` }} />
            </div>

            <div className="grid gap-3">
              {q.options.map(o => (
                <button key={o.value}
                  className="text-left p-4 bg-white/[0.03] hover:bg-accent-blue/10 border border-white/10 hover:border-accent-blue rounded-lg transition group"
                  onClick={() => choose(o.value)}
                  data-testid={`quiz-option-${o.value}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{o.label}</span>
                    <ArrowRight size={16} className="text-white/30 group-hover:text-accent-blue transition" />
                  </div>
                </button>
              ))}
            </div>

            {step > 0 && (
              <button onClick={back} className="mt-6 text-sm text-white/50 hover:text-white flex items-center gap-1">
                <ArrowLeft size={14} /> Précédent
              </button>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-accent-green/80 mb-3">
              <Sparkles size={13} /> Vos 3 meilleures prop firms
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-3" data-testid="quiz-results-title">
              Voici les prop firms qui correspondent le mieux à votre profil
            </h1>
            <p className="text-white/60 text-sm mb-8">
              Score sur 100. Cliquez pour voir chaque prop firm sur son site officiel.
            </p>

            <div className="space-y-3 mb-6" data-testid="quiz-results-list">
              {top3.map((f, i) => (
                <a key={f.id} href={buildLink(f)} target="_blank" rel="noopener noreferrer nofollow sponsored"
                   className="block p-5 panel hover:border-accent-blue/60 transition"
                   data-testid={`quiz-result-${f.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="text-3xl w-10 text-center">{i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"}</div>
                    <div className="text-3xl">{f.flag}</div>
                    <div className="flex-1">
                      <div className="font-heading font-bold text-lg">{f.name}</div>
                      <div className="text-xs text-white/50 mt-0.5">
                        Split {f.split}% · Payout {f.payoutDays}j · Max DD {f.maxDD}% · ${f.price}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold font-mono text-accent-green">{f.score}</div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider">Score</div>
                    </div>
                    <ExternalLink size={16} className="text-white/40 ml-2" />
                  </div>
                </a>
              ))}
            </div>

            <div className="flex gap-3">
              <button onClick={reset} className="btn btn-secondary" data-testid="quiz-restart">
                <RotateCcw size={14} /> Refaire le quiz
              </button>
              <Link to="/prop-firms" className="btn btn-primary" data-testid="quiz-view-all">
                Voir toutes les prop firms <ArrowRight size={14} />
              </Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
